// Presentation rules for a credential's state. Pure and separately testable: this repo's babel is
// preset-env only (no TypeScript preset), so keep it plain JS with no annotations or jest cannot
// parse it.

// Maps the API's `status` plus the failure code onto what a user should see. The API's own strings
// are internal vocabulary ("needs_attention"), never shown raw.
export const CREDENTIAL_STATUS = {
  ACTIVE: 'Active',
  NEEDS_ATTENTION: 'Needs attention',
  PAUSED: 'Paused',
  DISABLED: 'Disabled',
  UNVERIFIED: 'Not yet verified'
};

// A paused login is the API's own status ('Paused'): no failure code, because the customer may have nothing to fix.
export const isPausedCredential = credential => (credential === null || credential === void 0 ? void 0 : credential.status) === 'Paused' && !credential.disabledOn;

// "Notified": the sweep that pauses a login posts it to the eng alerts Slack channel (data-pipelines
// utility_bill_flow), and the bill queue lists the utility as automation failed.
// What pauses a login (property-service ApplyResultToCredential): 3 sign-ins in a row that failed for a reason other
// than the password. What restarts it: a new password (Reactivate) or staff Resume. Saving only details does not.
export const PAUSED_MESSAGE = "Paused: Livable stopped signing in after 3 tries in a row failed for reasons other than the password, like a captcha or a page it didn't recognize, so the provider doesn't lock this login. The password is probably fine. Saving a new password restarts it, and Livable's team can resume it. Changing only the address or instructions keeps it paused.";

/** Staff wording: they hold the Resume button. */
export const PAUSED_STAFF_MESSAGE = "Paused: Livable stopped signing in after 3 tries in a row failed for reasons other than the password, like a captcha or a page it didn't recognize. The password is probably fine. Resume to try again at the next daily collection, or save a new password if it changed (that resumes it too). Changing only the address or instructions keeps it paused.";

/** A raw API status (the queue's credentialStatus) in words, and whether someone has to act. */
export const credentialStatusCopy = status => {
  switch (status) {
    case 'Paused':
      return {
        problem: true,
        text: PAUSED_STAFF_MESSAGE
      };
    case 'ActionRequired':
      return {
        problem: true,
        text: 'Login failing: the provider rejected the saved login or wants a new password. Update the login with the current password.'
      };
    case 'MfaRequired':
      return {
        problem: true,
        text: 'Login failing: the provider asks for a verification code each time. Add how to get the code in the login instructions, or turn it off on the portal.'
      };
    case 'Disabled':
      return {
        problem: true,
        text: 'Login turned off: bills for this utility are collected by hand until the login is updated.'
      };
    default:
      return {
        problem: false,
        text: ''
      };
  }
};

// Order matters: disabled beats everything (an explicitly disabled credential is not "active" just
// because it last succeeded), a pause beats an old failure, and a failure beats a stale success.
export const credentialStatusLabel = credential => {
  if (!credential) return null;
  if (credential.disabledOn) return CREDENTIAL_STATUS.DISABLED;
  if (isPausedCredential(credential)) return CREDENTIAL_STATUS.PAUSED;
  if (credential.lastFailureCode) return CREDENTIAL_STATUS.NEEDS_ATTENTION;
  if (credential.lastVerifiedAt || credential.lastSucceededAt) return CREDENTIAL_STATUS.ACTIVE;
  return CREDENTIAL_STATUS.UNVERIFIED;
};
export const credentialStatusVariant = credential => {
  switch (credentialStatusLabel(credential)) {
    case CREDENTIAL_STATUS.ACTIVE:
      return 'success';
    // Names must be slots defined in src/styles/components/chip.ts. 'error' and 'default' are not,
    // so the two states that most need to stand out rendered unstyled.
    case CREDENTIAL_STATUS.NEEDS_ATTENTION:
      return 'critical';
    case CREDENTIAL_STATUS.DISABLED:
      return 'inactive';
    case CREDENTIAL_STATUS.PAUSED:
      return 'warning';
    default:
      return 'warning';
  }
};

// Failure codes are internal. Anything unrecognised must fall back to a generic message rather than
// leaking a raw code - some carry provider wording that can hint at the credential itself.
const SAFE_REASONS = {
  credential_failure: 'The provider rejected the saved login.',
  password_expired: 'The provider requires a password reset.',
  mfa_required: 'The provider requires multi-factor verification.',
  captcha_blocked: 'The provider challenged automated access.',
  infrastructure_failure: 'The provider could not be reached.'
};

// No failure copy on a paused login: the pause explains itself, and an older code would contradict the chip.
export const credentialFailureMessage = credential => {
  if (!(credential !== null && credential !== void 0 && credential.lastFailureCode) || isPausedCredential(credential)) return null;
  return SAFE_REASONS[credential.lastFailureCode] || 'The last check did not succeed.';
};
const formatDay = value => value ? new Date(value).toLocaleDateString() : null;

// A login on a provider no scraper covers carries this key (property-service UnscrapedProviderKey).
export const isAutomatedCredential = credential => !!(credential !== null && credential !== void 0 && credential.providerKey) && !credential.providerKey.startsWith('provider-');

// What each status means to the person looking at it, and what they can do about it.
export const credentialStatusTooltip = function (credential) {
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    isAutomated = _ref.isAutomated,
    staff = _ref.staff;
  if (!credential) return 'Add the portal login so Livable can collect this bill.';
  switch (credentialStatusLabel(credential)) {
    case CREDENTIAL_STATUS.DISABLED:
      {
        const day = formatDay(credential.disabledOn);
        return "Turned off".concat(day ? " on ".concat(day) : '', ". Bills for this utility are collected manually until the login is updated.");
      }
    case CREDENTIAL_STATUS.NEEDS_ATTENTION:
      return "".concat(credentialFailureMessage(credential), " Update the login to fix it.");
    case CREDENTIAL_STATUS.PAUSED:
      return staff ? PAUSED_STAFF_MESSAGE : PAUSED_MESSAGE;
    case CREDENTIAL_STATUS.ACTIVE:
      {
        const day = formatDay(credential.lastVerifiedAt || credential.lastSucceededAt);
        return day ? "Signed in to the provider successfully on ".concat(day, ".") : 'Signed in to the provider successfully.';
      }
    default:
      {
        // Checked but not yet confirmed. A clean check with nothing new and an inconclusive one (captcha, outage)
        // leave the status alike; the API says which it was.
        const checked = formatDay(credential.lastCheckedOn);
        if (checked && credential.lastCheckFoundNoNewBill) {
          return "Checked on ".concat(checked, ". No new bill was ready, so the login isn't confirmed yet. It's checked again at the next daily collection.");
        }
        if (checked && credential.statusReasonDisplay) {
          return "Checked on ".concat(checked, ": ").concat(credential.statusReasonDisplay, " It's checked again at the next daily collection.");
        }
        return isAutomated ? 'Saved, not used yet. It is checked the next time bills are collected, once a day.' : "Saved. Livable's team checks it the next time they collect this bill.";
      }
  }
};

// What the login is for, shown above the form.
export const credentialPurposeCopy = (providerName, isAutomated) => isAutomated ? "Bills for ".concat(providerName || 'this provider', " are retrieved automatically with this login.") : "Livable's team uses this login to collect your bills.";

// A credential belongs to this utility row only if the API said so. Never fall back to "same
// provider" - that would show one property's credential on another property's utility.
export const findCredentialForUtility = (credentials, propertyUtilityId) => {
  if (!(credentials !== null && credentials !== void 0 && credentials.length) || !propertyUtilityId) return null;
  return credentials.find(c => c.propertyUtilityId === propertyUtilityId) || null;
};

// The utilities-setup store keeps a saved property utility's id as `id` (its UtilityConfig model has no
// propertyUtilityId field, so MST drops one). Other surfaces pass objects that carry propertyUtilityId.
export const propertyUtilityIdOf = utility => (utility === null || utility === void 0 ? void 0 : utility.propertyUtilityId) || (utility === null || utility === void 0 ? void 0 : utility.id) || null;

// Property utilities come back from more than one endpoint and the provider/type ids sit at
// different depths depending on which. Read both shapes rather than assuming one.
const providerIdOf = utility => {
  var _utility$provider;
  return (utility === null || utility === void 0 || (_utility$provider = utility.provider) === null || _utility$provider === void 0 ? void 0 : _utility$provider.utilityProviderId) || (utility === null || utility === void 0 ? void 0 : utility.utilityProviderId) || null;
};
const utilityTypeIdsOf = utility => ((utility === null || utility === void 0 ? void 0 : utility.utilityTypes) || []).map(type => (type === null || type === void 0 ? void 0 : type.utilityTypeId) || (type === null || type === void 0 ? void 0 : type.id)).filter(Boolean);

/**
 * Find the property utility that a just-completed ADD flow created.
 *
 * The create endpoints do not reliably return the new id, so it is resolved by re-reading the
 * property's utilities and matching what the user chose. Returns null rather than guessing when the
 * match is ambiguous: the caller must never write a credential against the wrong utility, and a
 * credential with no propertyUtilityId could never be found again.
 */
export const findCreatedPropertyUtility = function (utilities) {
  let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    utilityProviderId = _ref2.utilityProviderId,
    utilityTypeId = _ref2.utilityTypeId;
  if (!Array.isArray(utilities) || !utilities.length || !utilityProviderId) return null;
  const sameProvider = utilities.filter(u => providerIdOf(u) === utilityProviderId && (u === null || u === void 0 ? void 0 : u.propertyUtilityId));
  if (!sameProvider.length) return null;
  if (utilityTypeId) {
    const sameType = sameProvider.filter(u => utilityTypeIdsOf(u).includes(utilityTypeId));
    if (sameType.length === 1) return sameType[0];
    // More than one utility for the same provider and type means the property already had one
    // before this save. Picking either is a coin flip, so decline.
    if (sameType.length > 1) return null;
    // Zero is not a "no": the list endpoint returns utilityTypes empty for a freshly created
    // utility. The type is a disambiguator, not a requirement, so fall through to the
    // provider-only rule below, which still refuses to choose between candidates.
  }
  return sameProvider.length === 1 ? sameProvider[0] : null;
};

/**
 * Is this provider covered by a bill scraper?
 *
 * `scraperProviderKey` is the only valid signal - never the display name, which produced
 * `burbank water power (bwp)` while the scraper leases `burbank`, and let a custom provider turn
 * free text into a key. Takes several shapes because the add path has no property utility yet.
 */
export const isScraperSupported = function () {
  for (var _len = arguments.length, providerLikes = new Array(_len), _key = 0; _key < _len; _key++) {
    providerLikes[_key] = arguments[_key];
  }
  return providerLikes.some(p => typeof (p === null || p === void 0 ? void 0 : p.scraperProviderKey) === 'string' && p.scraperProviderKey.length > 0);
};